import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store/reducers';
import * as rest from '../../services/rest';

import { ICreditPackage } from '../../types/billing';

export interface BillingState {
  packages: ICreditPackage[];
  isLoading: boolean;
  error: string | null;
  clientSecret: string | null;
  isProcessingPayment: boolean;
}

const initialState: BillingState = {
  packages: [],
  isLoading: false,
  error: null,
  clientSecret: null,
  isProcessingPayment: false,
};

/*=========================================
  To the backend
=========================================*/

export const getCreditPackages = createAsyncThunk('billing/getCreditPackages', async (_, { rejectWithValue }) => {
  try {
    return await rest.getCreditPackages();
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
  }
});

export const createPaymentIntent = createAsyncThunk('billing/createPaymentIntent', async (packageId: string, { rejectWithValue, getState }) => {
  try {
    const state = getState() as RootState;
    const JWT = state.auth.JWT;
    if (!JWT) throw new Error('No JWT token');

    return await rest.createPaymentIntent(JWT, packageId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
  }
});

/*=========================================
  To Stripe
=========================================*/

// Note: Stripe Elements and payment confirmation happen in React components
// using @stripe/stripe-js and @stripe/react-stripe-js libraries.
// Card details are collected via Stripe Elements and sent directly to Stripe.
// This Redux slice only manages the state - actual Stripe calls are in components.

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setProcessingPayment: (state, action: PayloadAction<boolean>) => {
      state.isProcessingPayment = action.payload;
    },
    clearClientSecret: (state) => {
      state.clientSecret = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch credit packages
      .addCase(getCreditPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCreditPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = action.payload;
      })
      .addCase(getCreditPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create payment intent
      .addCase(createPaymentIntent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientSecret = action.payload;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setProcessingPayment, clearClientSecret } = billingSlice.actions;
export default billingSlice.reducer;
